import { Controller } from 'stimulus';
import { templateContent, useTimeout } from '@/js/util';

// After the pointer leaves a held (link) toast, dismiss after a short grace
// period rather than restarting the full original duration.
const HOVER_GRACE_MS = 3000;

export const show = (template) => {
  const toast = templateContent(template);
  const event = new CustomEvent('ui--toast:show', {
    detail: { toast },
  });
  document.dispatchEvent(event);
};

export default class extends Controller {
  static values = { duration: Number };
  static classes = ['transition'];

  connect() {
    useTimeout(this);

    // Toasts sit in a pointer-events-none container so they don't block the
    // canvas; re-enable events on interactive toasts so the control stays clickable.
    if (this.#isInteractive()) {
      this.element.classList.add('pointer-events-auto');
    }

    if (this.durationValue) {
      this.timeoutId = this.timeout(this.durationValue, this.#fadeOut);
    }
  }

  remove() {
    this.element.remove();
  }

  reset() {
    if (this.durationValue) {
      this.#restart(this.durationValue);
    }
  }

  // Only toasts containing a link or button hold open while hovered (so the control
  // stays clickable); non-interactive toasts (e.g. success) just dismiss on schedule.
  pause() {
    if (!this.#canHold()) {
      return;
    }

    this.clearTimeout(this.timeoutId);
    this.element.classList.remove(...this.transitionClasses);
    this.element.classList.add('transition-none');
  }

  resume() {
    if (this.#canHold()) {
      this.#restart(HOVER_GRACE_MS);
    }
  }

  #canHold() {
    return Boolean(this.durationValue) && this.#isInteractive();
  }

  #isInteractive() {
    return this.element.querySelector('a, button') !== null;
  }

  #restart(delay) {
    this.element.classList.remove(...this.transitionClasses);
    this.element.classList.add('transition-none');
    this.clearTimeout(this.timeoutId);
    this.timeoutId = this.timeout(delay, this.#fadeOut);
  }

  #fadeOut() {
    this.element.classList.remove('transition-none');
    this.element.classList.add(...this.transitionClasses);
  }
}
